/* eslint-disable eslint-comments/disable-enable-pair -- need flat button */
/* eslint-disable @typescript-eslint/no-deprecated -- need flat button */
import type { ButtonHandle } from "@tql/ui-components-react/kendo/buttons";
import { Button as KendoButton } from "@tql/ui-components-react/kendo/buttons";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Popup } from "@tql/ui-components-react/kendo/popup";
import { dotsVerticalLine } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useRef, useState } from "react";

type ActionCellProps<T> = GridCustomCellProps & {
	onEdit?: (dataItem: T) => void;
	onAddAndClose?: (dataItem: T) => void;
	onRemoveFromList?: (dataItem: T) => void;
};

export const ActionCell = <T,>(props: ActionCellProps<T>) => {
	const { onEdit, onAddAndClose, onRemoveFromList, ...gridProps } = props;
	const anchor = useRef<ButtonHandle | null>(null);
	const [show, setShow] = useState(false);

	const handleOpenPopup = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setShow(true);
	};

	const handleClosePopup = useCallback(() => {
		setShow(false);
	}, []);

	const handleEditClick = useCallback(() => {
		if (onEdit && props.dataItem) {
			onEdit(props.dataItem as T);
		}
		handleClosePopup();
	}, [onEdit, props.dataItem, handleClosePopup]);

	const handleAddAndCloseClick = useCallback(() => {
		if (onAddAndClose && props.dataItem) {
			onAddAndClose(props.dataItem as T);
		}
		handleClosePopup();
	}, [onAddAndClose, props.dataItem, handleClosePopup]);

	const handleRemoveFromListClick = useCallback(() => {
		if (onRemoveFromList && props.dataItem) {
			onRemoveFromList(props.dataItem as T);
		}
		handleClosePopup();
	}, [onRemoveFromList, props.dataItem, handleClosePopup]);

	// Guard against undefined dataItem
	if (!props.dataItem) {
		return <td {...gridProps.tdProps}></td>;
	}

	const isLegacy = (props.dataItem as { isLegacy?: boolean }).isLegacy ?? false;

	return (
		<td {...gridProps.tdProps}>
			<KendoButton
				ref={anchor}
				data-testid={"grid-row-actions-button"}
				disabled={show}
				fillMode={"flat"}
				onClick={handleOpenPopup}
				svgIcon={dotsVerticalLine}
			/>
			<Popup
				anchor={anchor.current ? anchor.current.element : null}
				anchorAlign={{ horizontal: "right", vertical: "bottom" }}
				className={"action-menu"}
				onMouseDownOutside={handleClosePopup}
				popupAlign={{ horizontal: "right", vertical: "top" }}
				popupClass={"popup-content"}
				show={show}
			>
				{onEdit ? (
					<KendoButton
						data-testid={"grid-row-edit-button"}
						disabled={isLegacy}
						fillMode={"flat"}
						onClick={handleEditClick}
					>
						Edit
					</KendoButton>
				) : null}
				{onAddAndClose ? (
					<KendoButton
						data-testid={"grid-row-add-and-close-button"}
						fillMode={"flat"}
						onClick={handleAddAndCloseClick}
					>
						Add & Close
					</KendoButton>
				) : null}
				{onRemoveFromList ? (
					<KendoButton
						data-testid={"grid-row-remove-button"}
						disabled={isLegacy}
						fillMode={"flat"}
						onClick={handleRemoveFromListClick}
					>
						Remove from List
					</KendoButton>
				) : null}
			</Popup>
		</td>
	);
};
